import { useCallback, useEffect, useRef, useState } from "react";
import { useFileManager } from "./hooks/useFileManager";
import { DragDropProvider } from "./components/DropZone";
import { FileList } from "./components/FileList";
import { SearchBar } from "./components/SearchBar";
import { Document, CreateDocumentRequest } from "../../model/FileManager";
import { UploadButton } from "./components/UploadButton";
import { useDebounce } from "./utils/fileUtils";

interface MyFilesProps {
    onClose?: () => void;
    botId: string;
    bucketName: string;
}

export default function MyFiles({ onClose, bucketName, botId }: MyFilesProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [files, setFiles] = useState<Document[]>([]);
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [tenantId] = useState(`${botId}-${bucketName}`);
    const [pagination, setPagination] = useState({
        limit: 10,
        offset: 0,
        total: 0,
    });
    const modalRef = useRef<HTMLDivElement>(null);

    const {
        isLoading,
        error,
        uploadFile,
        deleteFile,
        fetchAllFiles,
    } = useFileManager();
/* eslint-disable react-hooks/exhaustive-deps */
const loadAllFiles = useCallback(async () => {
        const response = await fetchAllFiles(
            tenantId,
            pagination.limit,
            pagination.offset,
            debouncedSearchQuery
        );
        if (response) {
            setFiles(response.files);
            setPagination({
                limit: response.limit,
                offset: response.offset,
                total: response.total,
            });
        }
    }, [
        botId,
        bucketName,
        pagination.limit,
        pagination.offset,
        debouncedSearchQuery,
        fetchAllFiles,
    ]);

    useEffect(() => {loadAllFiles();}, [debouncedSearchQuery, pagination.offset]);

    /* eslint-enable react-hooks/exhaustive-deps */


    const handleSearch = (value: string) => {
        setSearchQuery(value);
        if (pagination.offset !== 0) {
            setPagination((prev) => ({ ...prev, offset: 0 }));
        }
    };
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && onClose) {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [onClose]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                modalRef.current &&
                !modalRef.current.contains(event.target as Node) &&
                onClose
            ) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handlePageChange = (newOffset: number) => {
        setPagination((prev) => ({ ...prev, offset: newOffset }));
    };

    const handleUploadFile = async (files: FileList) => {
        if (!files || files.length === 0) {
            alert("Please select files to upload");
            return;
        }

        try {
            setIsUploading(true);

            const allowedTypes = ["application/pdf"];
            const maxSize = 10 * 1024 * 1024;

            for (const file of files) {
                if (!allowedTypes.includes(file.type)) {
                    throw new Error(`File type ${file.type} is not supported`);
                }

                if (file.size > maxSize) {
                    throw new Error(
                        `File ${file.name} is too large. Maximum size is 5MB`
                    );
                }
                const fileRequest: CreateDocumentRequest = {
                    fileName: file.name,
                    contentType: file.type,
                    tenantId: tenantId,
                };

                await uploadFile(file, fileRequest);
            }

            await loadAllFiles();
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteFile = async (fileName: string) => {
        await deleteFile(fileName, tenantId);
        await loadAllFiles();
    };

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-red-500">
                    <p>Error: {error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[50]">
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div
                    ref={modalRef}
                    className="bg-white rounded-lg w-[90vw] xl:w-[1000px] max-w-4xl h-[70vh] flex flex-col relative mt-20"
                >
                    {isLoading && (
                        <div className="absolute inset-0 bg-white bg-opacity-70 z-50 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
                        </div>
                    )}
                    <DragDropProvider
                        onFilesDropped={handleUploadFile}
                        fileInputRef={fileInputRef}
                        isUploading={isUploading}
                    >
                        {/* Close button */}
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                                aria-label="Close"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        )}

                        {/* Header */}
                        <div className="p-4 border-b border-gray-200">
                            <h2 className="text-xl font-semibold">
                                {"Dokumenten-Eimer " + bucketName}
                            </h2>
                        </div>

                        {/* Content */}
                        <div className="p-4 flex-1 overflow-y-auto relative">
                            <div className="flex flex-col h-full">
                                <div className="flex items-center space-x-4 mb-4">
                                    <div className="flex-1">
                                        <SearchBar
                                            value={searchQuery}
                                            name={bucketName || ""}
                                            onChange={handleSearch}
                                        />
                                    </div>
                                    <UploadButton
                                        fileInputRef={fileInputRef}
                                        onFilesSelected={handleUploadFile}
                                        isUploading={isUploading}
                                    />
                                </div>
                                <div className="flex-1 overflow-y-auto">
                                    <FileList
                                        files={files}
                                        searchQuery={searchQuery}
                                        isLoading={isLoading}
                                        pagination={pagination}
                                        tenantId={tenantId}
                                        onPageChange={handlePageChange}
                                        onDelete={handleDeleteFile}
                                    />
                                </div>
                            </div>
                        </div>
                    </DragDropProvider>
                </div>
            </div>
        </div>
    );
}
